import { doc, onSnapshot, getFirestore, Timestamp } from "firebase/firestore";

// Constants
const DEFAULT_OPERATION_TIMEOUT = 30_000;
const DEFAULT_COLABORADOR_TRIGGERS_TIMEOUT = 120_000;

// eslint-disable-next-line import/no-unused-modules
export interface OperationStatus {
  operationId: string;
  operationType: string;
  entityId?: string;
  entityType?: string;
  status: "PROCESSING" | "COMPLETED" | "ERROR";
  step: string;
  error?: string;
  metadata?: Record<string, unknown>;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  completedAt?: Timestamp;
}

/**
 * Generic function to wait for any operation to complete
 * @param operationId - ID of the operation to track
 * @param timeoutMs - Timeout in milliseconds (default: 30 seconds)
 * @returns Promise that resolves when operation completes or rejects on timeout/error
 */
// eslint-disable-next-line import/no-unused-modules
export function waitForOperation(
  operationId: string,
  timeoutMs: number = DEFAULT_OPERATION_TIMEOUT
): Promise<OperationStatus> {
  // eslint-disable-next-line compat/compat
  return new Promise((resolve, reject) => {
    const database = getFirestore();
    const statusReference = doc(database, "operationStatus", operationId);

    let unsubscribe: (() => void) | null = null;

    // Set timeout
    const timeoutId = setTimeout(() => {
      if (unsubscribe) unsubscribe();
      //reject(new Error(`Timeout aguardando conclusão da operação ${operationId}`));
    }, timeoutMs);

    // Listen for status changes
    unsubscribe = onSnapshot(
      statusReference,
      (document_) => {
        if (!document_.exists()) {
          return; // Wait for document to be created by trigger
        }

        const status = document_.data() as OperationStatus;

        if (status.status === "COMPLETED") {
          clearTimeout(timeoutId);
          unsubscribe?.();
          resolve(status);
        } else if (status.status === "ERROR") {
          clearTimeout(timeoutId);
          unsubscribe?.();
          reject(new Error(status.error || "Erro desconhecido durante o processamento"));
        }
        // If still processing, continue listening
      },
      (error) => {
        clearTimeout(timeoutId);
        unsubscribe?.();
        reject(error);
      }
    );
  });
}

/**
 * Specific function for colaborador creation operations (backward compatibility)
 * @param colaboradorId - ID of the colaborador
 * @param timeoutMs - Timeout in milliseconds (default: 30 seconds)
 * @returns Promise that resolves when colaborador triggers complete
 */
// eslint-disable-next-line import/no-unused-modules
export function waitForColaboradorTriggers(
  colaboradorId: string,
  timeoutMs: number = DEFAULT_COLABORADOR_TRIGGERS_TIMEOUT
): Promise<OperationStatus> {
  return waitForOperation(colaboradorId, timeoutMs);
}
